// Fingerprint of the text + voice that produced an event's stored audio.
// `event.rendered` is kept for compatibility; UI, filters, and export should
// call `eventNeedsRender` so typing, find/replace, and voice changes are seen.

import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import { getEventTtsText } from "@app/lib/audio/renderEventAudio.js";
import { WEB_SPEECH_PROVIDER } from "@app/lib/audio/webSpeechTts.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";

const MANUAL_RECORD_NAME = "Manual Record";

function fnv1aHex(value) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * Hash of the TTS-relevant fields for an event (text, voice, model, style,
 * stability, similarity). Playback speed is excluded; it is post-render.
 *
 * @param {object} event
 * @returns {string}
 */
export function getAudioRenderHash(event) {
    if (!event) return "";
    const fingerprint = [
        getEventTtsText(event),
        voiceUtils.voiceKey(event.voice),
        voiceUtils.renderModel(event.voice, event.speakingStyle) || "",
        voiceUtils.renderStyle(event.voice, event.speakingStyle) || "",
        String(event.voiceStability ?? 50),
        String(event.voiceSimilarity ?? 50),
    ].join("\u001f");
    return fnv1aHex(fingerprint);
}

/**
 * Record the current fingerprint as the last successful render.
 * @param {object} event
 */
export function stampAudioRenderHash(event) {
    if (!event) return;
    event.audioRenderHash = getAudioRenderHash(event);
}

/**
 * True when stored audio is missing or no longer matches text/voice.
 * Web Speech is always live, so it never needs a stored render.
 *
 * @param {object} event
 * @returns {boolean}
 */
export function eventNeedsRender(event) {
    if (!event) return false;
    if (event.voice?.provider === WEB_SPEECH_PROVIDER) return false;

    const ttsText = getEventTtsText(event);
    const isManualRecord = event.voice?.name === MANUAL_RECORD_NAME;
    const hasAudio = Boolean(event.audioFile);

    if (!ttsText && !isManualRecord && !hasAudio) return false;
    if (!hasAudio) return Boolean(ttsText) || isManualRecord;

    if (event.audioRenderHash) {
        return event.audioRenderHash !== getAudioRenderHash(event);
    }

    return event.rendered === false;
}

/**
 * For legacy events that already have audio but no hash, snapshot the current
 * fingerprint so later edits are detected. Does not mark already-stale audio.
 *
 * @param {object} event
 */
export function backfillEventAudioRenderHash(event) {
    if (!event || event.audioRenderHash) return;
    if (!event.audioFile) return;
    event.audioRenderHash = getAudioRenderHash(event);
}

/**
 * Backfill hashes on audio description / audio dubbing groups.
 * @param {Array} eventGroups
 */
export function backfillAudioRenderHashes(eventGroups) {
    if (!Array.isArray(eventGroups)) return;
    for (const group of eventGroups) {
        if (!isAudioGroupType(group?.type) || !Array.isArray(group.events)) continue;
        for (const event of group.events) {
            backfillEventAudioRenderHash(event);
        }
    }
}
